// NoiseGenerator.js
// This utility class generates various types of noise for procedural terrain generation

import { createNoise2D, createNoise3D } from 'https://cdn.skypack.dev/simplex-noise@4.0.1';

/**
 * Utility class for generating different types of noise for procedural terrain generation
 */
class NoiseGenerator {
  /**
   * Generate simplex noise for a 2D grid
   * @param {number} width - Width of the grid
   * @param {number} height - Height of the grid
   * @param {number} scale - Scale of the noise (lower values = more zoomed out)
   * @param {number} octaves - Number of octaves for FBM (Fractal Brownian Motion)
   * @param {number} persistence - Persistence for FBM (amplitude multiplier)
   * @param {number} lacunarity - Lacunarity for FBM (frequency multiplier)
   * @param {string|number} seed - Seed for the noise generator
   * @returns {number[][]} 2D array of noise values between 0 and 1
   */
  static generateSimplexNoise(width, height, scale = 1, octaves = 1, persistence = 0.5, lacunarity = 2, seed = 'default') {
    // Initialize the noise function with the provided seed
    const noise2D = createNoise2D(() => this.hashSeed(seed));
    
    // Create a 2D array to store noise values
    const noiseMap = Array(height).fill().map(() => Array(width).fill(0));
    
    // Generate noise values
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Apply multiple octaves of noise for more natural-looking results
        let amplitude = 1;
        let frequency = 1;
        let noiseValue = 0;
        let maxValue = 0;
        
        for (let i = 0; i < octaves; i++) {
          const sampleX = x / scale * frequency;
          const sampleY = y / scale * frequency;
          
          // Add noise value for this octave, scaled by amplitude
          const simplexValue = noise2D(sampleX, sampleY);
          noiseValue += simplexValue * amplitude;
          
          // Keep track of the maximum possible value for normalization
          maxValue += amplitude;
          
          // Update amplitude and frequency for the next octave
          amplitude *= persistence;
          frequency *= lacunarity;
        }
        
        // Normalize the noise value to the range [0, 1]
        noiseValue = (noiseValue / maxValue + 1) / 2;
        noiseMap[y][x] = noiseValue;
      }
    }
    
    return noiseMap;
  }
  
  /**
   * Generate Perlin-like noise for a 2D grid
   * This is a convenience method that uses simplex noise with settings that approximate Perlin noise
   * @param {number} width - Width of the grid
   * @param {number} height - Height of the grid
   * @param {number} scale - Scale of the noise (lower values = more zoomed out)
   * @param {number} octaves - Number of octaves for FBM
   * @param {number} persistence - Persistence for FBM (amplitude multiplier)
   * @param {number} lacunarity - Lacunarity for FBM (frequency multiplier)
   * @param {string|number} seed - Seed for the noise generator
   * @returns {number[][]} 2D array of noise values between 0 and 1
   */
  static generatePerlinNoise(width, height, scale = 1, octaves = 1, persistence = 0.5, lacunarity = 2, seed = 'default') {
    // For this implementation, we use simplex noise with settings that approximate Perlin noise
    // Real Perlin noise would use a different interpolation method, but Simplex provides similar results
    return this.generateSimplexNoise(width, height, scale, octaves, persistence, lacunarity, seed);
  }
  
  /**
   * Generate 2D noise directly for a specific coordinate
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} scale - Scale of the noise
   * @param {string|number} seed - Seed for the noise generator
   * @returns {number} Noise value between 0 and 1
   */
  static generate2DNoise(x, y, scale = 1, seed = 'default') {
    const noise2D = createNoise2D(() => this.hashSeed(seed));
    const noiseValue = noise2D(x / scale, y / scale);
    return (noiseValue + 1) / 2; // Normalize to [0, 1]
  }
  
  /**
   * Generate 3D noise directly for a specific coordinate
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   * @param {number} scale - Scale of the noise
   * @param {string|number} seed - Seed for the noise generator
   * @returns {number} Noise value between 0 and 1
   */
  static generate3DNoise(x, y, z, scale = 1, seed = 'default') {
    const noise3D = createNoise3D(() => this.hashSeed(seed));
    const noiseValue = noise3D(x / scale, y / scale, z / scale);
    return (noiseValue + 1) / 2; // Normalize to [0, 1]
  }
  
  /**
   * Hash a seed string or number into a predictable number
   * @param {string|number} seed - The seed to hash
   * @returns {number} A number between 0 and 1
   */
  static hashSeed(seed) {
    // Convert the seed to a string
    const seedStr = seed.toString();
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < seedStr.length; i++) {
      const char = seedStr.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // Normalize to [0, 1]
    return Math.abs(hash) / 2147483647;
  }
  
  /**
   * Generate a preview of the noise map as a data URL for testing and visualization
   * @param {number[][]} noiseMap - 2D array of noise values between 0 and 1
   * @param {string} colorMap - Color mapping ('grayscale', 'terrain', 'heatmap')
   * @returns {string} Data URL of the generated image
   */
  static generateNoisePreview(noiseMap, colorMap = 'grayscale') {
    const width = noiseMap[0].length;
    const height = noiseMap.length;
    
    // Create a canvas to draw the noise map
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Create an image data object
    const imageData = ctx.createImageData(width, height);
    
    // Fill the image data based on the noise map
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const noiseValue = noiseMap[y][x];
        const index = (y * width + x) * 4;
        
        // Apply different color mappings
        if (colorMap === 'grayscale') {
          const value = Math.floor(noiseValue * 255);
          imageData.data[index] = value;     // R
          imageData.data[index + 1] = value; // G
          imageData.data[index + 2] = value; // B
          imageData.data[index + 3] = 255;   // A
        } 
        else if (colorMap === 'terrain') {
          // Blue for water, yellow for sand, green for land, white for mountains
          if (noiseValue < 0.3) {
            // Deep water
            imageData.data[index] = 0;
            imageData.data[index + 1] = 0;
            imageData.data[index + 2] = Math.floor(noiseValue * 850);
          } else if (noiseValue < 0.4) {
            // Shallow water
            imageData.data[index] = 0;
            imageData.data[index + 1] = Math.floor((noiseValue - 0.3) * 2550);
            imageData.data[index + 2] = Math.floor(noiseValue * 850);
          } else if (noiseValue < 0.45) {
            // Beach/sand
            imageData.data[index] = Math.floor(noiseValue * 555);
            imageData.data[index + 1] = Math.floor(noiseValue * 555);
            imageData.data[index + 2] = Math.floor((noiseValue - 0.4) * 1500);
          } else if (noiseValue < 0.75) {
            // Land/grass
            imageData.data[index] = Math.floor((noiseValue - 0.45) * 100); 
            imageData.data[index + 1] = Math.floor(noiseValue * 300);
            imageData.data[index + 2] = Math.floor((noiseValue - 0.45) * 100);
          } else {
            // Mountains
            const gray = Math.floor(((noiseValue - 0.75) * 4) * 255 + 100);
            imageData.data[index] = gray;
            imageData.data[index + 1] = gray;
            imageData.data[index + 2] = gray;
          }
          imageData.data[index + 3] = 255; // A
        }
        else if (colorMap === 'heatmap') {
          // Red to blue heatmap
          imageData.data[index] = Math.floor(noiseValue * 255);      // R
          imageData.data[index + 1] = Math.floor((1 - noiseValue) * 100); // G
          imageData.data[index + 2] = Math.floor((1 - noiseValue) * 255); // B
          imageData.data[index + 3] = 255;                           // A
        }
      }
    }
    
    // Put the image data on the canvas
    ctx.putImageData(imageData, 0, 0);
    
    // Return the data URL
    return canvas.toDataURL();
  }
}

export default NoiseGenerator; 